import { computed, unref } from "vue";
import { useQuery, useMutation, useQueryClient } from "@tanstack/vue-query";
import { API } from "@/services/apiInstance";
import { store } from "@/store";
import { useLocations, useUsers } from "./useReferenceData";
import { operationalFreshness } from "./queryFreshness";

export const announcementKeys = {
    all: ["announcements"],
    list: (status = "active") => ["announcements", "list", status],
    feed: () => ["announcements", "feed"],
    unacknowledged: () => ["announcements", "unacknowledged"],
    locations: () => ["announcements", "locations"],
    users: () => ["announcements", "users"]
};

/**
 * Fetch all announcements.
 */
export function useAnnouncements(statusRef = "active") {
    return useQuery({
        queryKey: computed(() => announcementKeys.list(unref(statusRef) || "active")),
        queryFn: async () =>
            (await API.Announcement.List({ status: unref(statusRef) || "active" })) || [],
        ...operationalFreshness
    });
}

export function useAnnouncementFeed(enabledRef = true) {
    return useQuery({
        queryKey: announcementKeys.feed(),
        queryFn: async () => (await API.Announcement.ListFeed()) || [],
        enabled: computed(() => Boolean(unref(enabledRef))),
        ...operationalFreshness
    });
}

export function useUnacknowledgedAnnouncements(enabledRef = true) {
    return useQuery({
        queryKey: announcementKeys.unacknowledged(),
        queryFn: async () => (await API.Announcement.ListUnacknowledged()) || [],
        enabled: computed(() => Boolean(unref(enabledRef))),
        ...operationalFreshness
    });
}

export function useAcknowledgeAnnouncement() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => API.Announcement.Acknowledge(id),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: announcementKeys.all })
    });
}

/**
 * Fetch locations for announcement targeting.
 */
export function useAnnouncementLocations() {
    return useLocations({
        select: (locations) =>
            locations.filter((location) => !location.tags?.includes("DEACTIVATED"))
    });
}

/**
 * Fetch users for acknowledgment tracking.
 */
export function useAnnouncementUsers() {
    return useUsers();
}

/**
 * Mutation to create an announcement.
 */
export function useCreateAnnouncement() {
    const queryClient = useQueryClient();

    return useMutation({
        // idempotencyKey dedupes server-side. Acknowledgement announcements also
        // send a push, so a retry must not create a second record or notification.
        mutationFn: ({ payload, idempotencyKey }) => API.Announcement.Add(payload, idempotencyKey),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: announcementKeys.all });
            store.state.$toast?.success("Announcement published successfully!");
        },
        onError: () => {
            store.state.$toast?.error("Failed to create announcement");
        }
    });
}

/**
 * Mutation to update an announcement.
 */
export function useUpdateAnnouncement() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }) => API.Announcement.Update(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: announcementKeys.all });
            store.state.$toast?.success("Announcement updated successfully!");
        },
        onError: () => {
            store.state.$toast?.error("Failed to update announcement");
        }
    });
}

/**
 * Mutation to delete an announcement.
 */
export function useDeleteAnnouncement() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id) => API.Announcement.Delete(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: announcementKeys.all });
            store.state.$toast?.success("Announcement archived successfully!");
        },
        onError: () => {
            store.state.$toast?.error("Failed to archive announcement");
        }
    });
}

export function useRestoreAnnouncement() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id) => API.Announcement.Update(id, { active: true }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: announcementKeys.all });
            store.state.$toast?.success("Announcement restored successfully!");
        },
        onError: () => {
            store.state.$toast?.error("Failed to restore announcement");
        }
    });
}
